<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { formatDate } from '@/utils/dateUtils';
import cloneDeep from 'lodash/cloneDeep';
import { getCurrentLocale } from '@/i18n';
import { tourTypeList } from '@/constants';
import capitalize from 'lodash/capitalize';
import Accordion from 'primevue/accordion';
import AccordionContent from 'primevue/accordioncontent';
import AccordionHeader from 'primevue/accordionheader';
import AccordionPanel from 'primevue/accordionpanel';
import Button from 'primevue/button';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';

interface Props {
  tourData: Array<any>;
  tourFilter: object;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  handleApply: [];
  handleReset: [value: any];
  'update:tourFilter': [value: any];
}>();

const currentLanguage = getCurrentLocale();
const cloneTours = ref(cloneDeep(props?.tourData));

const listTourFilter = computed({
  get() {
    return props.tourFilter;
  },
  set(value) {
    emits('update:tourFilter', value);
  }
});
const filterType = ref([]);

const convertTypePayload = (filterList: any) => {
  const typeArray = cloneDeep(filterList);
  for (let i = 0; i < typeArray.length; i++) {
    if (typeArray[i] === '-1') {
      typeArray[i] = '';
    }
  }
  return typeArray;
};

const handleFilter = () => {
  listTourFilter.value.type = convertTypePayload(filterType.value);
  emits('handleApply');
};

const handleReset = () => {
  const getAllType = listTypeFilter.map((type) => type.value);
  const defaultResetVal = {
    name: listTourNameFilter,
    type: convertTypePayload(getAllType),
    city: listCitiesFilter
  };
  filterType.value = getAllType;
  emits('handleReset', defaultResetVal);
};

const convertType = computed(() => {
  const typeArray = [];
  tourTypeList.forEach((type) => {
    cloneTours.value.map((tour) => {
      if (type.value == tour.type) {
        typeArray.push(type);
      } else if (!tour.type) {
        typeArray.push(tourTypeList[0]);
      }
    });
  });
  return typeArray;
});
const listDepDateFilter = [...new Set(cloneTours.value.map((tour) => tour.depDate).filter((depDate) => depDate !== null && depDate != ''))].sort((first, second) => new Date(first) - new Date(second));
const listDurationsFilter = [...new Set(cloneTours.value.map((tour) => tour.tourDurations).filter((duration) => duration !== null && duration != ''))].sort((first, second) => first - second);
const listTourNameFilter = [...new Set(cloneTours.value.map((tour) => tour[`tourName${capitalize(currentLanguage)}`]).filter((tourName) => tourName !== null && tourName != ''))].sort();
const listTypeFilter = [...new Set(convertType.value)];
const listCitiesFilter = [
  ...new Set(
    cloneTours.value.flatMap((tour) => {
      return tour[`regionsVisited${capitalize(currentLanguage)}`]
        .split(',')
        .map((city) => city.trim())
        .filter((city) => city !== '' && city !== null);
    })
  )
].sort();

const setDefaultFilterValue = () => {
  listTourFilter.value[`tourName${capitalize(currentLanguage)}`] = listTourNameFilter;
  listTypeFilter.forEach((type: any) => {
    filterType.value.push(type.value);
  });
  listTourFilter.value.type = convertTypePayload(filterType.value);
  listTourFilter.value[`regionsVisited${capitalize(currentLanguage)}`] = listCitiesFilter;
};

onMounted(() => {
  setDefaultFilterValue();
});
</script>
<template>
  <div class="tour-quote-search-filter">
    <Accordion value="0">
      <AccordionPanel value="0">
        <AccordionHeader>
          <span class="ml-4">{{ $t('common.filter') }}</span>
        </AccordionHeader>
        <AccordionContent>
          <div class="filter_bar flex justify-content-start gap-3">
            <div class="filter_option flex flex-column gap-3 w-2">
              <label class="long-text font-bold" for="depDate">{{ $t('multi_day_tour.date_of_departure') }}</label>
              <Select v-model="listTourFilter.depDate" inputId="depDate" :options="listDepDateFilter">
                <template #option="slotProps">
                  {{ formatDate(slotProps.option, 'dd-LLL-yyyy', true, currentLanguage) }}
                </template>
                <template #value="slotProps">
                  <span v-if="!slotProps.value">{{ $t('multi_day_tour.date_of_departure') }}</span>
                  <span v-else>{{ formatDate(slotProps.value, 'dd-LLL-yyyy', true, currentLanguage) }}</span>
                </template>
              </Select>
            </div>
            <div class="filter_option flex flex-column gap-3 w-2">
              <label class="long-text font-bold" for="duration">{{ $t('worksheet.duration') }}</label>
              <Select v-model="listTourFilter.tourDurations" inputId="duration" :options="listDurationsFilter" :placeholder="$t('worksheet.duration')" />
            </div>
            <div class="filter_option flex flex-column gap-3 w-2">
              <label for="tourName" class="long-text font-bold">{{ $t('management.tour.tour_name') }}</label>
              <MultiSelect inputId="tourName" :maxSelectedLabels="1" :placeholder="$t('management.tour.tour_name')" display="chip" v-model="listTourFilter[`tourName${capitalize(currentLanguage)}`]" :options="listTourNameFilter" class="w-full">
              </MultiSelect>
            </div>
            <div class="filter_option flex flex-column gap-3 w-2 filter-type-class">
              <label for="tourType" class="long-text font-bold">{{ $t('multi_day_tour.tour_type') }}</label>
              <MultiSelect
                inputId="tourType"
                :maxSelectedLabels="2"
                :showCheckbox="true"
                display="chip"
                v-model="filterType"
                :optionLabel="`name${capitalize(currentLanguage)}`"
                optionValue="value"
                :placeholder="$t('multi_day_tour.tour_type')"
                :options="listTypeFilter"
                class="w-full"
              >
              </MultiSelect>
            </div>
            <div class="filter_option flex flex-column gap-3 w-2 filter-cities">
              <label for="cities" class="long-text font-bold">{{ $t('multi_day_tour.citites_to_visit') }}</label>
              <MultiSelect
                inputId="cities"
                :maxSelectedLabels="2"
                :placeholder="$t('multi_day_tour.citites_to_visit')"
                display="chip"
                v-model="listTourFilter[`regionsVisited${capitalize(currentLanguage)}`]"
                :options="listCitiesFilter"
                class="w-full"
              >
              </MultiSelect>
            </div>
            <div class="filter_option flex gap-3">
              <div class="flex align-items-end">
                <Button class="run-filter" :label="$t('common.labels.run')" @click="handleFilter()"></Button>
              </div>
              <div class="flex align-items-end">
                <Button class="reset-filter" :label="$t('common.labels.reset')" @click="handleReset()"></Button>
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
  </div>
</template>
